import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import config from '../utils/config';

function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    folderUrl: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const subjectOptions = [
    'General Question',
    'Technical Support',
    'Report Content',
    'Request Feature',
    'Others'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Submit all contact forms (including content reports) to backend
      const response = await fetch(`${config.apiUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          subject: '',
          folderUrl: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    const requiredFields = ['name', 'email', 'subject', 'message'];
    if (formData.subject === 'Report Content') {
      requiredFields.push('folderUrl');
    }
    return requiredFields.every(field => formData[field].trim() !== '');
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: '#1a1d29', 
      color: '#e2e8f0',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/"
            style={{
              color: '#9ca3af',
              fontSize: '14px',
              textDecoration: 'none',
              marginBottom: '16px',
              display: 'inline-block'
            }}
          >
            ← Back to Home
          </Link>
          
          <h1 style={{ 
            fontSize: '32px', 
            fontWeight: 'bold', 
            marginBottom: '8px',
            color: '#e2e8f0'
          }}>
            Contact Us
          </h1>
          
          <p style={{ color: '#9ca3af', fontSize: '16px' }}>
            We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>

        {/* Contact Form */}
        <div style={{ 
          backgroundColor: '#242938', 
          border: '1px solid #374151',
          borderRadius: '3px',
          padding: '32px',
          lineHeight: '1.6'
        }}>
          <form onSubmit={handleSubmit}>
            {/* Name Field */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                color: '#e2e8f0', 
                fontWeight: '500' 
              }}>
                Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  backgroundColor: '#1a1d29',
                  border: '1px solid #374151',
                  borderRadius: '3px',
                  color: '#e2e8f0',
                  fontSize: '16px',
                  outline: 'none',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#374151'}
              />
            </div>

            {/* Email Field */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                color: '#e2e8f0', 
                fontWeight: '500' 
              }}>
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  backgroundColor: '#1a1d29',
                  border: '1px solid #374151',
                  borderRadius: '3px',
                  color: '#e2e8f0',
                  fontSize: '16px',
                  outline: 'none',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#374151'}
              />
            </div>

            {/* Subject Field */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                color: '#e2e8f0', 
                fontWeight: '500' 
              }}>
                Subject *
              </label>
              <select
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  backgroundColor: '#1a1d29',
                  border: '1px solid #374151',
                  borderRadius: '3px',
                  color: '#e2e8f0',
                  fontSize: '16px',
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  cursor: 'pointer'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#374151'}
              >
                <option value="">Select a subject</option>
                {subjectOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            {/* Folder URL Field - Only for Report Content */}
            {formData.subject === 'Report Content' && (
              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  color: '#e2e8f0', 
                  fontWeight: '500' 
                }}>
                  Folder URL to Report *
                </label>
                <input
                  type="url"
                  name="folderUrl"
                  value={formData.folderUrl}
                  onChange={handleInputChange}
                  required
                  placeholder="https://largetransfer.net/d/..."
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    backgroundColor: '#1a1d29',
                    border: '1px solid #374151',
                    borderRadius: '3px',
                    color: '#e2e8f0',
                    fontSize: '16px',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#374151'}
                />
                <p style={{ 
                  color: '#9ca3af', 
                  fontSize: '14px', 
                  marginTop: '8px',
                  marginBottom: '0'
                }}>
                  Please provide the exact URL of the content you want to report. Our team will review it and take appropriate action.
                </p>
              </div>
            )}

            {/* Message Field */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                color: '#e2e8f0', 
                fontWeight: '500' 
              }}>
                Message *
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                rows={6}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  backgroundColor: '#1a1d29',
                  border: '1px solid #374151',
                  borderRadius: '3px',
                  color: '#e2e8f0',
                  fontSize: '16px',
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#374151'}
              />
            </div>

            {/* Submit Button */}
            <div style={{ marginBottom: '24px' }}>
              <button
                type="submit"
                disabled={!isFormValid() || isSubmitting}
                style={{
                  backgroundColor: isFormValid() && !isSubmitting ? '#3b82f6' : '#6b7280',
                  color: '#ffffff',
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '3px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: isFormValid() && !isSubmitting ? 'pointer' : 'not-allowed',
                  transition: 'background-color 0.2s ease',
                  minWidth: '120px'
                }}
                onMouseEnter={(e) => {
                  if (isFormValid() && !isSubmitting) {
                    e.target.style.backgroundColor = '#2563eb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (isFormValid() && !isSubmitting) {
                    e.target.style.backgroundColor = '#3b82f6';
                  }
                }}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </div>

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div style={{
                backgroundColor: '#065f46',
                border: '1px solid #10b981',
                borderRadius: '3px',
                padding: '12px 16px',
                color: '#d1fae5',
                marginBottom: '16px'
              }}>
                {formData.subject === 'Report Content' ? (
                  <>
                    ✓ Content report submitted successfully! Our team will review the reported content and take appropriate action within 24-48 hours. Thank you for helping maintain a safe environment.
                  </>
                ) : (
                  <>
                    ✓ Message sent successfully! We'll get back to you soon.
                  </>
                )}
              </div>
            )}

            {submitStatus === 'error' && (
              <div style={{
                backgroundColor: '#7f1d1d',
                border: '1px solid #ef4444',
                borderRadius: '3px',
                padding: '12px 16px',
                color: '#fecaca',
                marginBottom: '16px'
              }}>
                ✗ Failed to send message. Please try again or contact us directly at {config.contactEmail}.
              </div>
            )}
          </form>
        </div>

        {/* Contact Information */}
        <div style={{ 
          backgroundColor: '#242938', 
          border: '1px solid #374151',
          borderRadius: '3px',
          padding: '24px',
          marginTop: '24px'
        }}>
          <h3 style={{ 
            color: '#3b82f6', 
            marginBottom: '16px', 
            fontSize: '18px', 
            fontWeight: '600' 
          }}>
            Other Ways to Reach Us
          </h3>
          <p style={{ color: '#d1d5db', marginBottom: '8px' }}>
            <strong>Email:</strong> {config.contactEmail}
          </p>
          <p style={{ color: '#d1d5db', marginBottom: '0' }}>
            <strong>Response Time:</strong> We typically respond within 24-48 hours.
          </p>
        </div>

        {/* Footer Navigation */}
        <div className="mt-8 text-center">
          <Link 
            to="/privacy-policy"
            style={{
              color: '#9ca3af',
              fontSize: '14px',
              textDecoration: 'none',
              marginRight: '24px'
            }}
          >
            Privacy Policy
          </Link>
          
          <Link 
            to="/terms-of-service"
            style={{
              color: '#9ca3af',
              fontSize: '14px',
              textDecoration: 'none',
              marginRight: '24px'
            }}
          >
            Terms of Service
          </Link>
          
          <Link 
            to="/"
            style={{
              color: '#9ca3af',
              fontSize: '14px',
              textDecoration: 'none'
            }}
          >
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ContactForm;
